import type {
  NoteLine,
  ParsedScore,
  PartAssignment,
  RawNote,
  TempoPoint,
  Timeline,
  TimelineMeasure,
  TimelineNote,
  VoicePart,
} from './model';
import { unrollMeasures } from './unroll';

/** Used when a score has no tempo mark at all. */
export const DEFAULT_QUARTER_BPM = 100;

export interface TimelineOptions {
  /** Playback speed relative to the marked tempo, e.g. 0.5 to 1. Default 1. */
  tempoFactor?: number;
  /** Role of each part by part id (from the confirmed summary). Parts not listed are accompaniment. */
  assignments?: Record<string, PartAssignment>;
}

function round(x: number): number {
  return Math.round(x * 1e9) / 1e9;
}

/** Converts beats (quarter notes) to seconds along a tempo map. */
export function secondsAtBeat(tempoMap: TempoPoint[], beat: number, tempoFactor = 1): number {
  let point = tempoMap[0];
  if (!point) return 0;
  for (const p of tempoMap) {
    if (p.beat <= beat) point = p;
    else break;
  }
  return point.sec + ((beat - point.beat) * 60) / (point.quarterBpm * tempoFactor);
}

/**
 * Build the linear note timeline: repeats and voltas unrolled, times in beats and seconds,
 * sounding pitches, lyric syllables for each pass, and the written measure and beat offset
 * of each note for cursor mapping.
 */
export function buildTimeline(score: ParsedScore, options: TimelineOptions = {}): Timeline {
  const tempoFactor = options.tempoFactor ?? 1;
  const { order } = unrollMeasures(score.measures);

  // Measures in playback order.
  const measures: TimelineMeasure[] = [];
  let beat = 0;
  for (const step of order) {
    const m = score.measures[step.measureIndex];
    if (!m) continue;
    measures.push({
      timelineIndex: measures.length,
      measureIndex: step.measureIndex,
      measureNumber: m.number,
      pass: step.pass,
      startBeat: round(beat),
      durationBeats: m.length,
      startSec: 0,
      durationSec: 0,
    });
    beat += m.length;
  }
  const totalBeats = round(beat);

  // Tempo map: every tempo mark, at each place its measure is played.
  const raw: { beat: number; quarterBpm: number }[] = [];
  for (const tm of measures) {
    for (const mark of score.tempoMarks) {
      if (mark.measureIndex === tm.measureIndex) {
        raw.push({ beat: round(tm.startBeat + mark.offset), quarterBpm: mark.quarterBpm });
      }
    }
  }
  raw.sort((a, b) => a.beat - b.beat);
  const tempoMap: TempoPoint[] = [{ beat: 0, sec: 0, quarterBpm: raw[0]?.quarterBpm ?? DEFAULT_QUARTER_BPM }];
  for (const r of raw) {
    const last = tempoMap[tempoMap.length - 1] as TempoPoint;
    if (r.beat === last.beat) {
      last.quarterBpm = r.quarterBpm;
    } else if (r.quarterBpm !== last.quarterBpm) {
      const sec = round(last.sec + ((r.beat - last.beat) * 60) / (last.quarterBpm * tempoFactor));
      tempoMap.push({ beat: r.beat, sec, quarterBpm: r.quarterBpm });
    }
  }
  const toSec = (b: number): number => round(secondsAtBeat(tempoMap, b, tempoFactor));
  for (const tm of measures) {
    tm.startSec = toSec(tm.startBeat);
    tm.durationSec = round(toSec(tm.startBeat + tm.durationBeats) - tm.startSec);
  }

  // Two or more staves assigned the same voice part (Soprano 1 and Soprano 2) are its lines, from the top.
  const staffLine = new Map<string, NoteLine>();
  const partsByVoice = new Map<VoicePart, string[]>();
  for (const part of score.parts) {
    const assignment = options.assignments?.[part.id];
    if (!assignment || assignment === 'accompaniment') continue;
    partsByVoice.set(assignment, [...(partsByVoice.get(assignment) ?? []), part.id]);
  }
  for (const ids of partsByVoice.values()) {
    if (ids.length > 1) ids.forEach((id, i) => staffLine.set(id, i === 0 ? 1 : i === 1 ? 2 : 3));
  }

  // Notes, with tied notes merged into one sounding note.
  const notes: TimelineNote[] = [];
  for (const part of score.parts) {
    const assignment = options.assignments?.[part.id];
    const voicePart: VoicePart | null = assignment && assignment !== 'accompaniment' ? assignment : null;
    const partLine = staffLine.get(part.id);
    const byMeasure = new Map<number, RawNote[]>();
    for (const note of part.notes) {
      const list = byMeasure.get(note.measureIndex);
      if (list) list.push(note);
      else byMeasure.set(note.measureIndex, [note]);
    }
    const openTies = new Map<string, TimelineNote>();
    for (const tm of measures) {
      for (const note of byMeasure.get(tm.measureIndex) ?? []) {
        const startBeat = round(tm.startBeat + note.offset);
        const key = `${note.staff}|${note.voice}|${note.midi}`;
        const open = openTies.get(key);
        if (note.tieStop && open && Math.abs(open.startBeat + open.durationBeats - startBeat) < 1e-6) {
          // A note held across a change of division stays one note for the line that holds it;
          // the other line gets its own note for the part where both sang it.
          const held = partLine === undefined && (note.line === 1 || note.line === 2) ? note.line : null;
          const holder = open.line === 1 || open.line === 2 ? open.line : null;
          if (open.line === 0 && held !== null) {
            notes.push({ ...open, line: held === 1 ? 2 : 1 });
            open.line = held;
          } else if (holder !== null && note.line === 0 && partLine === undefined) {
            notes.push({
              ...open,
              startBeat,
              durationBeats: note.duration,
              measureIndex: note.measureIndex,
              measureNumber: tm.measureNumber,
              beatOffset: note.offset,
              timelineMeasureIndex: tm.timelineIndex,
              lyric: null,
              line: holder === 1 ? 2 : 1,
            });
          }
          open.durationBeats = round(open.durationBeats + note.duration);
          if (!note.tieStart) openTies.delete(key);
          continue;
        }
        const lyric =
          note.lyrics.find((l) => l.verse === tm.pass) ??
          [...note.lyrics].sort((a, b) => a.verse - b.verse)[0] ??
          null;
        const timelineNote: TimelineNote = {
          partId: part.id,
          voicePart,
          staff: note.staff,
          voice: note.voice,
          midi: note.midi,
          spelling: note.spelling,
          startBeat,
          durationBeats: note.duration,
          startSec: 0,
          durationSec: 0,
          measureIndex: note.measureIndex,
          measureNumber: tm.measureNumber,
          beatOffset: note.offset,
          timelineMeasureIndex: tm.timelineIndex,
          lyric,
          line: partLine ?? note.line,
        };
        notes.push(timelineNote);
        if (note.tieStart) openTies.set(key, timelineNote);
        else openTies.delete(key);
      }
    }
  }
  for (const note of notes) {
    note.startSec = toSec(note.startBeat);
    note.durationSec = round(toSec(note.startBeat + note.durationBeats) - note.startSec);
  }
  notes.sort(
    (a, b) =>
      a.startBeat - b.startBeat ||
      (a.partId < b.partId ? -1 : a.partId > b.partId ? 1 : 0) ||
      a.staff - b.staff ||
      a.midi - b.midi,
  );

  return { measures, notes, tempoMap, totalBeats, totalSec: toSec(totalBeats), tempoFactor };
}

/** Frequency in Hz of a (possibly fractional) MIDI pitch for a reference A4. */
export function midiToFrequency(midi: number, referenceA4 = 440): number {
  return referenceA4 * Math.pow(2, (midi - 69) / 12);
}
